// Z Caddy: side-arrow click handler for the main product gallery.
// Walks the visible thumbnail list (StarApps hides non-variant ones via display:none,
// so offsetParent filtering naturally restricts navigation to the current variant)
// and triggers the next/prev thumbnail's click — Dawn's setActiveMedia handles the rest.
// See PROJECT-LOG.md (2026-04-30) for context on why we navigate via thumbnails.
document.addEventListener('click', (event) => {
  const arrow = event.target.closest('[data-zc-gallery-direction]');
  if (!arrow) return;
  // Stop the click from reaching the modal-opener that wraps the gallery image —
  // without these, the click bubbles up and Dawn's product-modal opens.
  event.preventDefault();
  event.stopPropagation();
  const gallery = arrow.closest('media-gallery');
  if (!gallery) return;

  const direction = arrow.dataset.zcGalleryDirection;

  // Path A — desktop: walk visible thumbnails (StarApps hides non-variant ones via
  // display:none, so offsetParent filtering restricts to the current variant) and
  // click the next thumbnail's button. This invokes setActiveMedia + StarApps listeners.
  const thumbContainer = Array.from(gallery.querySelectorAll('[id^="GalleryThumbnails"]')).find(
    (el) => el.offsetParent !== null
  );
  if (thumbContainer) {
    const thumbs = Array.from(thumbContainer.querySelectorAll('[data-target]')).filter(
      (t) => t.offsetParent !== null
    );
    if (thumbs.length >= 2) {
      const currentIdx = thumbs.findIndex((t) => t.querySelector('[aria-current="true"]'));
      const nextIdx =
        direction === 'next'
          ? (Math.max(currentIdx, 0) + 1) % thumbs.length
          : (Math.max(currentIdx, 0) - 1 + thumbs.length) % thumbs.length;
      thumbs[nextIdx].querySelector('button')?.click();
      return;
    }
  }

  // Path B — mobile (thumbnail row is hidden via small-hide): walk visible media items
  // and call setActiveMedia directly. On mobile, items in the active variant set
  // have non-null offsetParent (StarApps hides non-variant items via display:none).
  const visibleViewer = Array.from(gallery.querySelectorAll('[id^="GalleryViewer"]')).find(
    (el) => el.offsetParent !== null
  );
  if (!visibleViewer) return;
  const items = Array.from(visibleViewer.querySelectorAll('.product__media-item')).filter(
    (item) => item.offsetParent !== null
  );
  if (items.length < 2) return;
  const currentIdx = items.findIndex((item) => item.classList.contains('is-active'));
  const nextIdx =
    direction === 'next'
      ? (Math.max(currentIdx, 0) + 1) % items.length
      : (Math.max(currentIdx, 0) - 1 + items.length) % items.length;
  const nextMediaId = items[nextIdx].dataset.mediaId;
  if (typeof gallery.setActiveMedia === 'function') {
    gallery.setActiveMedia(nextMediaId, false);
  }
});

if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        if (prepend) {
          activeMedia.parentElement.prepend(activeMedia);
          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.prepend(activeThumbnail);
          }
          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
            activeMedia.scrollIntoView({ behavior: 'smooth' });
          }
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
